import {BehaviorSubject, map, Observable, of, tap} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {inject} from "@angular/core";
import DataResponse, {MetaInformation, RequestOptions} from "../DataResponse";
import Model from "../../model/model";

export default abstract class Store<T> {
  protected data: BehaviorSubject<Record<number, T>> = new BehaviorSubject<Record<number, T>>({});
  public meta: BehaviorSubject<MetaInformation | null> = new BehaviorSubject<MetaInformation | null>(null);

  protected name: string = this.constructor.name;
  private http: HttpClient;

  constructor() {
    this.http = inject(HttpClient);
  }

  public add(data: T & Model): void {
    if (!data) {
      return;
    }

    const currentData: Record<number, T> = this.data.getValue() ?? {};
    const newDataSet: Record<number, T> = currentData;

    const previousOptions: Record<string, unknown> = currentData[data.id] ? (currentData[data.id] as T & Model).options : {};
    data.options = {...previousOptions, ...data.options} as Record<string, null>
    data.fetched_at = new Date();

    newDataSet[data.id] = data;

    this.data.next(newDataSet)
  }

  public all(options: RequestOptions = {}, onlyCached: boolean = false): Observable<T[]> {

    let url: string = '/' + this.name;
    if (options) {
      url += "?" + new URLSearchParams(options as Record<string, string>).toString();
    }

    if (!onlyCached) {
      this.http.get(url).subscribe((response: object) => {
        const dataResponse: DataResponse<T[]> = response as DataResponse<T[]>;
        (dataResponse.data ?? []).forEach((entry: T) => {
          const record: T & Model = entry as T & Model;
          record.options = options as Record<string, null>;
          this.add(record);
        });

        this.meta.next(null);

        if (dataResponse.meta) {
          this.meta.next(dataResponse.meta);
        }
      });
    }

    return this.data
      .asObservable().pipe(
        map((data: Record<number, T>) => {
          return Object.values(data ?? {}).filter(
            (entry: T) => {
              const record: T & Model = entry as T & Model;

              //get all records where the have the same properties underneath options as the request
              let isMatching: boolean = true;
              Object.keys(options).forEach((key: string) => {
                if (record.options[key] !== options[key]) {
                  isMatching = false;
                }
              });

              return isMatching;
            }
          );
        })
      );
  }

  public get(requestedId: number | null, options?: RequestOptions): Observable<T | null> {

    if (!requestedId) {
      return of(null);
    }

    //if there is no record in the data, create an empty record
    if (!this.data.getValue()[requestedId]) {
      const record: T & Model = {} as T & Model;
      record.id = requestedId;
      if (options) {
        record.options = options as Record<string, null>;
      }

      this.add(record);
    }

    //fetch the record from the server if the options are not the same
    const record: T & Model = this.data.getValue()[requestedId] as T & Model;
    //check if current object contains no other fields than options, id and fetched_at
    const fieldsWithinObject: string[] = Object.keys(record).filter((key: string) => {
      return !["options", "id", "fetched_at"].includes(key);
    });

    if (options && record.options !== options || fieldsWithinObject.length === 0) {
      let url: string = this.name + "/" + requestedId
      if (options) {
        url += "?" + new URLSearchParams(options as Record<string, string>).toString();
      }
      this.http.get(url).subscribe((response: object) => {
        const record: T & Model = (response as DataResponse<T>).data as T & Model;
        this.add(record);
      });
    }

    return this.data.asObservable().pipe(
      map((data: Record<number, T>) => {
        return data[requestedId];
      })
    );
  }

  public create(newRecord: T, requestOptions: RequestOptions = {}): Observable<T> {
    const payload: DataResponse<T & Model> = DataResponse.fromObject(newRecord as T & Model);
    let url: string = '/' + this.name;

    if (requestOptions && requestOptions.include) {
      url += "?" + new URLSearchParams(requestOptions as Record<string, string>).toString();
    }

    return this.http.post(url, payload).pipe(
      tap((response: object) => {
        const data: DataResponse<T> = response as DataResponse<T>;
        this.add(data.data as T & Model);
      }),
      map((response: object) => {
        return (response as DataResponse<T>).data as T;
      })
    );
  }

  public update(record: T & Model): Observable<T> {
    const payload: DataResponse<T & Model> = DataResponse.fromObject(record as T & Model);

    return this.http.put("/" + this.name + "/" + record.id, payload).pipe(
      tap((response: object) => {
        const data: DataResponse<T> = response as DataResponse<T>;
        this.add(data.data as T & Model);
      }),
      map((response: object) => {
        return (response as DataResponse<T>).data as T;
      })
    );
  }

  public delete(record: T & Model): Observable<T> {

    return this.http.delete("/" + this.name + "/" + record.id).pipe(
      tap((response: object) => {
        const data: DataResponse<T> = response as DataResponse<T>;
        this.add(data.data as T & Model);
      }),
      map((response: object) => {
        return (response as DataResponse<T>).data as T;
      })
    );
  }

  public forParent(parentName: string, parent: Model): Store<T> {
    this.name = parentName + "/" + parent.id + "/" + this.name;
    return this;
  }

  public reset(): Store<T> {
    this.name = this.name.split("/").pop() ?? this.name;
    return this;
  }
}
